import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { SettingsContext, UserContext } from '@/state/contexts';
import { useLogoutMutation } from '@/state/slices/auth';
import type { InactivityTimeout } from '@/models/settings';
import { performClientLogout } from '@/state/utils/authSession';

const TIMEOUT_MS: Record<InactivityTimeout, number | null> = {
  '15_MINUTES': 15 * 60_000,
  '30_MINUTES': 30 * 60_000,
  '1_HOUR': 60 * 60_000,
  '2_HOURS': 120 * 60_000,
  OFF: null,
};

const WARNING_LEAD_MS = 60_000;
const THROTTLE_MS = 1_000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

export function InactivityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, setUser } = useContext(UserContext);
  const { settings } = useContext(SettingsContext);
  const [logoutFn] = useLogoutMutation();

  const [showWarning, setShowWarning] = useState(false);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const lastActivityRef = useRef(Date.now());
  const showWarningRef = useRef(false);

  const isAuthenticated = !!user?.token;
  const timeoutMs = TIMEOUT_MS[settings.inactivityTimeout] ?? null;
  const isActive = isAuthenticated && timeoutMs !== null;

  const forceLogout = useCallback(() => {
    performClientLogout(logoutFn, setUser);
    window.location.assign('/login');
  }, [logoutFn, setUser]);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
  }, []);

  const startTimers = useCallback(() => {
    if (!timeoutMs) return;
    clearTimers();

    const warningDelay = Math.max(timeoutMs - WARNING_LEAD_MS, 0);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, warningDelay);

    logoutTimerRef.current = setTimeout(() => {
      setShowWarning(false);
      forceLogout();
    }, timeoutMs);
  }, [timeoutMs, clearTimers, forceLogout]);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  useEffect(() => {
    if (!isActive) {
      clearTimers();
      setShowWarning(false);
      return undefined;
    }

    startTimers();

    let lastThrottle = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastThrottle < THROTTLE_MS) return;
      lastThrottle = now;
      lastActivityRef.current = now;

      // While warning is open, ignore general activity so users can still click
      // "Stay Logged In". Session continues only from that explicit action.
      if (showWarningRef.current) {
        return;
      }
      startTimers();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, onActivity, { passive: true })
    );

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, onActivity)
      );
    };
  }, [isActive, startTimers, clearTimers, forceLogout]);

  const handleStayLoggedIn = () => {
    resetTimers();
  };

  return (
    <>
      {children}
      <Modal
        title="Session Timeout Warning"
        open={showWarning}
        onOk={handleStayLoggedIn}
        okText="Stay Logged In"
        closable={false}
        maskClosable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>
          You&apos;re about to be logged out due to inactivity. Click to stay
          logged in.
        </p>
      </Modal>
    </>
  );
}
